import { supabase } from '../supabase';
import type { UserConnection, UserMessage, UserProfile, UserGame, Game } from '../../types';

/**
 * Serviço para gerenciar funcionalidades de comunidade e interação entre usuários
 */
class CommunityService {
  /**
   * Busca o perfil público de um usuário pelo username
   * 
   * @param username Nome de usuário a ser buscado
   * @returns O perfil do usuário ou null se não encontrado
   */
  async getUserProfileByUsername(username: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', username)
        .single();

      if (error) {
        console.error('Erro ao buscar perfil:', error);
        return null;
      }

      return data as UserProfile;
    } catch (error) {
      console.error('Erro ao buscar perfil:', error);
      return null;
    }
  }

  /**
   * Busca os jogos da coleção de um usuário
   * 
   * @param userId ID do usuário
   * @returns Lista de jogos da coleção
   */
  async getUserCollection(userId: string): Promise<UserGame[]> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .select(`
          *,
          game:games!user_games_game_id_fkey(*)
        `)
        .eq('user_id', userId)
        .eq('owned', true);

      if (error) {
        console.error('Erro ao buscar coleção:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar coleção:', error);
      return [];
    }
  }

  /**
   * Busca os jogos da lista de desejos de um usuário
   * 
   * @param userId ID do usuário
   * @returns Lista de jogos desejados
   */
  async getUserWishlist(userId: string): Promise<UserGame[]> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .select(`
          *,
          game:games!user_games_game_id_fkey(*)
        `)
        .eq('user_id', userId)
        .eq('wishlist', true);

      if (error) {
        console.error('Erro ao buscar lista de desejos:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar lista de desejos:', error);
      return [];
    }
  }

  /**
   * Busca os jogos jogados por um usuário
   * 
   * @param userId ID do usuário
   * @returns Lista de jogos jogados
   */
  async getUserPlayedGames(userId: string): Promise<UserGame[]> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .select(`
          *,
          game:games!user_games_game_id_fkey(*)
        `)
        .eq('user_id', userId)
        .gt('play_count', 0);

      if (error) {
        console.error('Erro ao buscar jogos jogados:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar jogos jogados:', error);
      return [];
    }
  }

  /**
   * Busca os jogos avaliados por um usuário
   * 
   * @param userId ID do usuário
   * @returns Lista de jogos avaliados
   */
  async getUserRatedGames(userId: string): Promise<UserGame[]> {
    try {
      const { data, error } = await supabase
        .from('user_games')
        .select(`
          *,
          game:games!user_games_game_id_fkey(*)
        `)
        .eq('user_id', userId)
        .not('rating', 'is', null);

      if (error) {
        console.error('Erro ao buscar jogos avaliados:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar jogos avaliados:', error);
      return [];
    }
  }

  /**
   * Verifica o status de conexão entre dois usuários
   * 
   * @param userId ID do usuário atual
   * @param targetUserId ID do usuário alvo
   * @returns Dados da conexão ou null se não existir
   */
  async getConnectionStatus(userId: string, targetUserId: string): Promise<UserConnection | null> {
    try {
      const { data, error } = await supabase
        .from('user_connections')
        .select('*')
        .or(
          `and(user_id.eq.${userId},connected_user_id.eq.${targetUserId}),` +
          `and(user_id.eq.${targetUserId},connected_user_id.eq.${userId})`
        );
      
      if (error || !data || data.length === 0) {
        return null;
      }
      
      // Retorna a primeira conexão encontrada
      return data[0] as UserConnection;
    } catch (error) {
      console.error('Erro ao verificar status de conexão:', error);
      return null;
    }
  }

  /**
   * Envia uma solicitação de conexão para outro usuário
   * 
   * @param userId ID do usuário que está enviando a solicitação
   * @param targetUserId ID do usuário que receberá a solicitação
   * @returns Resultado da operação
   */
  async sendConnectionRequest(userId: string, targetUserId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // Verificar se já existe uma conexão entre os usuários
      const existingConnection = await this.getConnectionStatus(userId, targetUserId);
      
      // Se já existir uma conexão, retorna erro
      if (existingConnection) {
        return {
          success: false,
          error: 'Já existe uma conexão ou solicitação pendente com este usuário'
        };
      }
      
      // Criar nova solicitação de conexão
      const { error } = await supabase
        .from('user_connections')
        .insert({
          user_id: userId,
          connected_user_id: targetUserId,
          status: 'pending',
          created_at: new Date().toISOString()
        });
      
      if (error) {
        console.error('Erro ao enviar solicitação de conexão:', error);
        return {
          success: false,
          error: 'Não foi possível enviar a solicitação de conexão'
        };
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Erro ao enviar solicitação de conexão:', error);
      return {
        success: false,
        error: error.message || 'Erro desconhecido'
      };
    }
  }

  /**
   * Atualiza o status de uma conexão
   * 
   * @param connectionId ID da conexão
   * @param status Novo status
   * @param userId ID do usuário que está atualizando (para verificação)
   * @returns Resultado da operação
   */
  async updateConnectionStatus(
    connectionId: string, 
    status: 'accepted' | 'rejected' | 'blocked', 
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Buscar conexão para verificar se o usuário pode alterá-la
      const { data: connection, error: fetchError } = await supabase
        .from('user_connections')
        .select('*')
        .eq('id', connectionId)
        .single();

      if (fetchError) {
        console.error('Erro ao buscar conexão:', fetchError);
        return { success: false, error: 'Conexão não encontrada' };
      }

      // Verificar se o usuário é o destinatário da solicitação
      if (connection.connected_user_id !== userId) {
        return { 
          success: false, 
          error: 'Apenas o destinatário pode atualizar o status da solicitação'
        };
      }

      // Atualizar status
      const { error } = await supabase
        .from('user_connections')
        .update({ status })
        .eq('id', connectionId);

      if (error) {
        console.error('Erro ao atualizar conexão:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Erro ao atualizar conexão:', error);
      return { success: false, error: error.message || 'Erro desconhecido' };
    }
  }

  /**
   * Remove uma conexão usando o ID da conexão
   * 
   * @param connectionId ID da conexão a ser removida
   * @param userId ID do usuário que está removendo (para verificação)
   * @returns Resultado da operação
   */
  async removeConnection(connectionId: string, userId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // Verificar se a conexão existe
      const { data: connection, error: fetchError } = await supabase
        .from('user_connections')
        .select('*')
        .eq('id', connectionId)
        .single();
      
      if (fetchError) {
        console.error('Erro ao buscar conexão:', fetchError);
        return {
          success: false,
          error: 'Conexão não encontrada'
        };
      }
      
      // Verificar se o usuário é parte da conexão
      if (connection.user_id !== userId && connection.connected_user_id !== userId) {
        return {
          success: false,
          error: 'Você não tem permissão para remover esta conexão'
        };
      }
      
      // Remover a conexão
      const { error } = await supabase
        .from('user_connections')
        .delete()
        .eq('id', connectionId);
      
      if (error) {
        console.error('Erro ao remover conexão:', error);
        return {
          success: false,
          error: 'Não foi possível remover a conexão'
        };
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Erro ao remover conexão:', error);
      return {
        success: false,
        error: error.message || 'Erro desconhecido'
      };
    }
  }

  /**
   * Remove uma conexão entre dois usuários
   * 
   * @deprecated Use removeConnection(connectionId, userId) instead
   * @param userId ID do usuário atual
   * @param targetUserId ID do usuário para desconectar
   * @returns Resultado da operação
   */
  async removeConnectionByUserIds(userId: string, targetUserId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // Buscar a conexão existente
      const connection = await this.getConnectionStatus(userId, targetUserId);
      
      if (!connection) {
        return {
          success: false,
          error: 'Não existe uma conexão para remover'
        };
      }
      
      // Usar o método principal
      return await this.removeConnection(connection.id, userId);
    } catch (error: any) {
      console.error('Erro ao remover conexão:', error);
      return {
        success: false,
        error: error.message || 'Erro desconhecido'
      };
    }
  }

  /**
   * Busca todas as conexões de um usuário
   * 
   * @param userId ID do usuário
   * @param status Status opcional para filtrar (aceita, pendente, etc)
   * @returns Lista de conexões do usuário
   */
  async getUserConnections(userId: string, status?: string): Promise<UserConnection[]> {
    try {
      let query = supabase
        .from('user_connections')
        .select(`
          *,
          profile_user:profiles!user_connections_user_id_fkey(*),
          profile_connected:profiles!user_connections_connected_user_id_fkey(*)
        `)
        .or(
          `user_id.eq.${userId},connected_user_id.eq.${userId}`
        );
      
      // Aplicar filtro de status se fornecido
      if (status) {
        query = query.eq('status', status);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Erro ao buscar conexões:', error);
        return [];
      }
      
      // Processar os resultados para retornar no formato esperado pela interface
      return (data || []).map(connection => {
        // Determinar qual perfil é o do usuário conectado (não o atual)
        const isUserRequester = connection.user_id === userId;
        const connectedProfile = isUserRequester 
          ? connection.profile_connected 
          : connection.profile_user;
          
        return {
          ...connection,
          connected_profile: connectedProfile
        };
      });
    } catch (error) {
      console.error('Erro ao buscar conexões:', error);
      return [];
    }
  }

  /**
   * Obtém as solicitações de conexão pendentes para um usuário
   * 
   * @param userId ID do usuário
   * @returns Lista de solicitações pendentes
   */
  async getPendingConnectionRequests(userId: string): Promise<UserConnection[]> {
    try {
      const { data, error } = await supabase
        .from('user_connections')
        .select(`
          *,
          profile_user:profiles!user_connections_user_id_fkey(*),
          profile_connected:profiles!user_connections_connected_user_id_fkey(*)
        `)
        .eq('connected_user_id', userId)
        .eq('status', 'pending');
      
      if (error) {
        console.error('Erro ao buscar solicitações pendentes:', error);
        return [];
      }
      
      // Processar os resultados para retornar no formato esperado
      return (data || []).map(connection => {
        return {
          ...connection,
          connected_profile: connection.profile_user // Quem enviou a solicitação
        };
      });
    } catch (error) {
      console.error('Erro ao buscar solicitações pendentes:', error);
      return [];
    }
  }

  /**
   * Obtém o número de solicitações de conexão pendentes para um usuário
   * 
   * @param userId ID do usuário
   * @returns Número de solicitações pendentes
   */
  async getPendingConnectionRequestCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('user_connections')
        .select('*', { count: 'exact', head: true })
        .eq('connected_user_id', userId)
        .eq('status', 'pending');
      
      if (error) {
        console.error('Erro ao contar solicitações pendentes:', error);
        return 0;
      }
      
      return count || 0;
    } catch (error) {
      console.error('Erro ao contar solicitações pendentes:', error);
      return 0;
    }
  }

  /**
   * Obtém todas as conexões aceitas de um usuário
   * 
   * @param userId ID do usuário
   * @returns Lista de conexões aceitas
   */
  async getAcceptedConnections(userId: string): Promise<UserConnection[]> {
    try {
      // Buscar conexões com status "accepted" onde o usuário é o solicitante ou destinatário
      const { data, error } = await supabase
        .from('user_connections')
        .select(`
          *,
          profile_user:profiles!user_connections_user_id_fkey(*),
          profile_connected:profiles!user_connections_connected_user_id_fkey(*)
        `)
        .eq('status', 'accepted')
        .or(
          `user_id.eq.${userId},connected_user_id.eq.${userId}`
        );
      
      if (error) {
        console.error('Erro ao buscar conexões aceitas:', error);
        return [];
      }
      
      // Processar os resultados para retornar no formato esperado
      return (data || []).map(connection => {
        const isUserRequester = connection.user_id === userId;
        const connectedProfile = isUserRequester 
          ? connection.profile_connected 
          : connection.profile_user;
          
        return {
          ...connection,
          connected_profile: connectedProfile
        };
      });
    } catch (error) {
      console.error('Erro ao buscar conexões aceitas:', error);
      return [];
    }
  }

  /**
   * Envia uma mensagem para outro usuário
   * 
   * @param senderId ID do remetente
   * @param recipientId ID do destinatário
   * @param content Conteúdo da mensagem
   * @returns Resultado da operação
   */
  async sendMessage(
    senderId: string, 
    recipientId: string, 
    content: string
  ): Promise<{
    success: boolean;
    data?: UserMessage;
    error?: string;
  }> {
    try {
      // Verificar se existe uma conexão aceita entre os usuários
      const { data, error } = await supabase
        .from('user_connections')
        .select('*')
        .or(
          `and(user_id.eq.${senderId},connected_user_id.eq.${recipientId}),` +
          `and(user_id.eq.${recipientId},connected_user_id.eq.${senderId})`
        )
        .eq('status', 'accepted');

      // Verificar se alguma das consultas encontrou uma conexão
      if (error || !data || data.length === 0) {
        return { 
          success: false, 
          error: 'Você precisa ter uma conexão aceita com este usuário para enviar mensagens' 
        };
      }

      // Criar mensagem
      const { data: message, error: msgError } = await supabase
        .from('user_messages')
        .insert({
          sender_id: senderId,
          recipient_id: recipientId,
          content,
          read: false
        })
        .select()
        .single();

      if (msgError) {
        console.error('Erro ao enviar mensagem:', msgError)
        return { 
          success: false, 
          error: 'Erro ao enviar mensagem'
        };
      }

      return {
        success: true,
        data: message as UserMessage
      };
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      return {
        success: false,
        error: 'Erro ao enviar mensagem'
      };
    }
  }

  /**
   * Busca as mensagens trocadas entre dois usuários
   * 
   * @param userId ID do usuário atual
   * @param otherUserId ID do outro usuário
   * @returns Mensagens trocadas entre os usuários
   */
  async getMessages(userId: string, otherUserId: string): Promise<UserMessage[]> {
    try {
      const { data, error } = await supabase
        .from('user_messages')
        .select('*')
        .or(`and(sender_id.eq.${userId},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${userId})`)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Erro ao buscar mensagens:', error);
        return [];
      }

      return data as UserMessage[];
    } catch (error) {
      console.error('Erro ao buscar mensagens:', error);
      return [];
    }
  }

  /**
   * Marca uma mensagem como lida
   * 
   * @param messageId ID da mensagem
   * @param userId ID do usuário (deve ser o destinatário)
   * @returns Resultado da operação
   */
  async markMessageAsRead(
    messageId: string, 
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Verificar se o usuário é o destinatário
      const { data: message, error: fetchError } = await supabase
        .from('user_messages')
        .select('*')
        .eq('id', messageId)
        .single();

      if (fetchError) {
        console.error('Erro ao buscar mensagem:', fetchError);
        return { success: false, error: 'Mensagem não encontrada' };
      }

      if (message.recipient_id !== userId) {
        return { 
          success: false, 
          error: 'Apenas o destinatário pode marcar a mensagem como lida'
        };
      }

      // Marcar como lida
      const { error } = await supabase
        .from('user_messages')
        .update({ read: true })
        .eq('id', messageId);

      if (error) {
        console.error('Erro ao marcar mensagem como lida:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Erro ao marcar mensagem como lida:', error);
      return { success: false, error: error.message || 'Erro desconhecido' };
    }
  }

  /**
   * Marca todas as mensagens de um remetente específico como lidas
   * 
   * @param senderId ID do remetente
   * @param recipientId ID do destinatário
   * @returns Resultado da operação
   */
  async markAllMessagesAsRead(
    senderId: string, 
    recipientId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('user_messages')
        .update({ read: true })
        .eq('sender_id', senderId)
        .eq('recipient_id', recipientId)
        .eq('read', false);

      if (error) {
        console.error('Erro ao marcar mensagens como lidas:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Erro ao marcar mensagens como lidas:', error);
      return { success: false, error: error.message || 'Erro desconhecido' };
    }
  }

  /**
   * Conta o número de mensagens não lidas para um usuário
   * 
   * @param userId ID do usuário
   * @returns Número de mensagens não lidas
   */
  async getUnreadMessageCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('user_messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('read', false);

      if (error) {
        console.error('Erro ao contar mensagens não lidas:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Erro ao contar mensagens não lidas:', error);
      return 0;
    }
  }

  /**
   * Busca usuários com base em um termo de pesquisa
   * 
   * @param searchTerm Termo para buscar (username, email, nome)
   * @returns Lista de perfis encontrados
   */
  async searchUsers(searchTerm: string): Promise<UserProfile[]> {
    try {
      // Remover caracteres especiais e preparar para busca case-insensitive
      const sanitizedTerm = searchTerm.trim();
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .or(
          `and(username.ilike.%${sanitizedTerm}%),and(full_name.ilike.%${sanitizedTerm}%),and(email.ilike.%${sanitizedTerm}%)`
        )
        .order('username', { ascending: true })
        .limit(20);

      if (error) {
        console.error('Erro ao buscar usuários:', error);
        return [];
      }

      return data as UserProfile[];
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      return [];
    }
  }

  /**
   * Obtém as solicitações de conexão enviadas por um usuário
   * 
   * @param userId ID do usuário
   * @returns Lista de solicitações enviadas
   */
  async getSentConnectionRequests(userId: string): Promise<UserConnection[]> {
    try {
      const { data, error } = await supabase
        .from('user_connections')
        .select(`
          *,
          profile_user:profiles!user_connections_user_id_fkey(*),
          profile_connected:profiles!user_connections_connected_user_id_fkey(*)
        `)
        .eq('user_id', userId)
        .eq('status', 'pending');
      
      if (error) {
        console.error('Erro ao buscar solicitações enviadas:', error);
        return [];
      }
      
      // Processar os resultados para retornar no formato esperado
      return (data || []).map(connection => {
        return {
          ...connection,
          connected_profile: connection.profile_connected // Quem recebeu a solicitação
        };
      });
    } catch (error) {
      console.error('Erro ao buscar solicitações enviadas:', error);
      return [];
    }
  }

  /**
   * Busca usuários que tenham um determinado jogo na sua wishlist
   * 
   * @param gameId ID do jogo a ser buscado
   * @returns Lista de perfis de usuários que desejam o jogo
   */
  async getUsersByWishlistGame(gameId: string): Promise<UserProfile[]> {
    try {
      if (!gameId) {
        console.error('ID do jogo é obrigatório para buscar usuários.');
        return [];
      }

      // Busca IDs de usuários que têm o jogo na wishlist
      const { data: userGames, error: userGamesError } = await supabase
        .from('user_games')
        .select('user_id')
        .eq('game_id', gameId)
        .eq('wishlist', true);

      if (userGamesError) {
        console.error('Erro ao buscar usuários com o jogo na wishlist:', userGamesError);
        return [];
      }

      if (!userGames || userGames.length === 0) {
        return [];
      }

      // Extrair IDs de usuários
      const userIds = userGames.map(item => item.user_id);
      
      // Buscar perfis completos dos usuários
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', userIds);
      
      if (profilesError) {
        console.error('Erro ao buscar perfis dos usuários:', profilesError);
        return [];
      }

      return profiles || [];
    } catch (error) {
      console.error('Erro ao buscar usuários por jogo na wishlist:', error);
      return [];
    }
  }
}

// Exporta a classe como default
export default CommunityService; 